<div class="feature-mapping-container">
  <!-- Header -->
  <div class="page-header">
    <div class="header-content">
      <h3>Feature to Router Mapping</h3>
      <p class="subtitle">Configure how bit features affect the manufacturing router</p>
    </div>
    <button kendoButton themeColor="primary" (click)="createNewMapping()">
      <span class="k-icon k-font-icon k-i-plus"></span> New Mapping
    </button>
  </div>

  <!-- Filters Bar -->
  <div class="filters-bar">
    <div class="filter-group">
      <label>Template</label>
      <kendo-dropdownlist
        [(ngModel)]="filterTemplateId"
        [data]="templates"
        textField="name"
        valueField="id"
        [valuePrimitive]="true"
        [defaultItem]="{ name: 'All Templates', id: null }"
        (valueChange)="applyFilters()"
      ></kendo-dropdownlist>
    </div>

    <div class="filter-group">
      <label>Feature</label>
      <kendo-dropdownlist
        [(ngModel)]="filterFeatureId"
        [data]="bitFeatures"
        [textField]="'name'"
        [valueField]="'id'"
        [valuePrimitive]="true"
        [defaultItem]="{ name: 'All Features', id: null }"
        [filterable]="true"
        (valueChange)="applyFilters()"
      ></kendo-dropdownlist>
    </div>

    <div class="filter-group">
      <label>Action</label>
      <kendo-dropdownlist
        [(ngModel)]="filterAction"
        [data]="actionOptions"
        textField="label"
        valueField="value"
        [valuePrimitive]="true"
        [defaultItem]="{ label: 'All Actions', value: null }"
        (valueChange)="applyFilters()"
      ></kendo-dropdownlist>
    </div>

    <div class="filter-group search-group">
      <label>Search</label>
      <kendo-textbox
        [(ngModel)]="searchText"
        placeholder="Search mappings..."
        (valueChange)="applyFilters()"
      >
        <ng-template kendoTextBoxPrefixTemplate>
          <span class="k-icon k-font-icon k-i-search"></span>
        </ng-template>
      </kendo-textbox>
    </div>

    <button kendoButton fillMode="flat" (click)="clearFilters()" class="clear-filters-btn">
      <span class="k-icon k-font-icon k-i-filter-clear"></span> Clear
    </button>
  </div>

  <!-- Mappings Grid -->
  <div class="mappings-panel">
    <div class="mappings-grid" *ngIf="!loading && filteredMappings.length > 0">
      <div
        *ngFor="let mapping of filteredMappings"
        class="mapping-card"
        [class.inactive]="!mapping.isActive"
      >
        <div class="mapping-action-indicator" [style.backgroundColor]="getActionColor(mapping.action)">
          <span class="k-icon" [ngClass]="getActionIcon(mapping.action)"></span>
        </div>

        <div class="mapping-content">
          <div class="mapping-header">
            <div class="mapping-feature">
              <span class="feature-label">When feature:</span>
              <span class="feature-name">{{ mapping.bitFeatureName || getBitFeatureName(mapping.bitFeatureId) }}</span>
            </div>
            <span class="mapping-action-badge" [style.backgroundColor]="getActionColor(mapping.action)">
              {{ getActionLabel(mapping.action) }}
            </span>
          </div>

          <div class="mapping-details">
            <div class="detail-row" *ngIf="mapping.routerTemplateId">
              <span class="detail-label">Template:</span>
              <span class="detail-value">{{ mapping.routerTemplateName || getTemplateName(mapping.routerTemplateId) }}</span>
            </div>
            <div class="detail-row" *ngIf="!mapping.routerTemplateId">
              <span class="detail-label">Template:</span>
              <span class="detail-value all-templates">All Templates</span>
            </div>

            <div class="detail-row" *ngIf="mapping.targetStepDescription || mapping.targetStepId">
              <span class="detail-label">Target Step:</span>
              <span class="detail-value">{{ mapping.targetStepDescription || 'Step #' + mapping.targetStepId }}</span>
            </div>

            <div class="detail-row" *ngIf="mapping.newStepDescription">
              <span class="detail-label">New Step:</span>
              <span class="detail-value">{{ mapping.newStepDescription }}</span>
            </div>

            <div class="detail-row" *ngIf="mapping.newStepWorkCenter">
              <span class="detail-label">Work Center:</span>
              <span class="detail-value">{{ mapping.newStepWorkCenter }}</span>
            </div>

            <div class="detail-row" *ngIf="mapping.priority !== 0">
              <span class="detail-label">Priority:</span>
              <span class="detail-value">{{ mapping.priority }}</span>
            </div>
          </div>

          <div class="mapping-condition" *ngIf="mapping.condition">
            <span class="k-icon k-font-icon k-i-filter"></span>
            <span>{{ mapping.condition }}</span>
          </div>
        </div>

        <div class="mapping-actions">
          <button kendoButton size="small" fillMode="flat" (click)="editMapping(mapping)" title="Edit">
            <span class="k-icon k-font-icon k-i-edit"></span>
          </button>
          <button kendoButton size="small" fillMode="flat" (click)="duplicateMapping(mapping)" title="Duplicate">
            <span class="k-icon k-font-icon k-i-copy"></span>
          </button>
          <button kendoButton size="small" fillMode="flat" (click)="deleteMapping(mapping)" title="Delete">
            <span class="k-icon k-font-icon k-i-delete"></span>
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div class="empty-state" *ngIf="!loading && filteredMappings.length === 0">
      <span class="k-icon k-font-icon k-i-link-horizontal k-icon-xl"></span>
      <p *ngIf="mappings.length === 0">No feature mappings configured yet.</p>
      <p *ngIf="mappings.length > 0">No mappings match your filters.</p>
      <button kendoButton themeColor="primary" (click)="createNewMapping()" *ngIf="mappings.length === 0">
        Create First Mapping
      </button>
    </div>

    <!-- Loading -->
    <div class="loading-state" *ngIf="loading">
      <kendo-loader size="medium"></kendo-loader>
      <span>Loading mappings...</span>
    </div>
  </div>
</div>

<!-- Edit/Create Dialog -->
<kendo-dialog
  *ngIf="isEditing"
  [title]="isNew ? 'Create Feature Mapping' : 'Edit Feature Mapping'"
  (close)="cancelEdit()"
  [width]="700"
>
  <form [formGroup]="mappingForm" class="mapping-form">
    <!-- Feature Selection -->
    <div class="form-section">
      <h4>Feature Selection</h4>
      <div class="form-row">
        <label>Bit Feature *</label>
        <kendo-dropdownlist
          formControlName="bitFeatureId"
          [data]="bitFeatures"
          textField="name"
          valueField="id"
          [valuePrimitive]="true"
          [filterable]="true"
        >
          <ng-template kendoDropDownListItemTemplate let-dataItem>
            <span class="feature-code">{{ dataItem.code }}</span>
            <span class="feature-name">{{ dataItem.name }}</span>
          </ng-template>
        </kendo-dropdownlist>
      </div>
    </div>

    <!-- Action Selection -->
    <div class="form-section">
      <h4>Action</h4>
      <div class="action-options">
        <div
          *ngFor="let option of actionOptions"
          class="action-option"
          [class.selected]="mappingForm.get('action')?.value === option.value"
          (click)="mappingForm.patchValue({ action: option.value }); onActionChange()"
        >
          <span class="k-icon" [ngClass]="option.icon" [style.color]="getActionColor(option.value)"></span>
          <div class="action-text">
            <span class="action-label">{{ option.label }}</span>
            <span class="action-description">{{ option.description }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Template & Step Selection -->
    <div class="form-section">
      <h4>Target</h4>
      <div class="form-row-group">
        <div class="form-row">
          <label>Router Template</label>
          <kendo-dropdownlist
            formControlName="routerTemplateId"
            [data]="templates"
            textField="name"
            valueField="id"
            [valuePrimitive]="true"
            [defaultItem]="{ name: 'All Templates', id: null }"
            (valueChange)="onTemplateChange()"
          ></kendo-dropdownlist>
          <span class="field-hint">Leave empty to apply to all templates</span>
        </div>

        <div class="form-row" *ngIf="requiresTargetStep(mappingForm.get('action')?.value)">
          <label>Target Step *</label>
          <kendo-dropdownlist
            formControlName="targetStepId"
            [data]="steps"
            textField="description"
            valueField="id"
            [valuePrimitive]="true"
            [disabled]="!mappingForm.get('routerTemplateId')?.value"
          >
            <ng-template kendoDropDownListItemTemplate let-dataItem>
              <span class="step-sequence">{{ dataItem.sequence }}</span>
              <span class="step-description">{{ dataItem.description }}</span>
            </ng-template>
          </kendo-dropdownlist>
        </div>
      </div>
    </div>

    <!-- New Step Configuration (for Add, Insert, Replace) -->
    <div class="form-section" *ngIf="requiresNewStepData(mappingForm.get('action')?.value)">
      <h4>New Step Configuration</h4>
      <div class="form-row">
        <label>Step Description *</label>
        <kendo-textbox formControlName="newStepDescription" placeholder="Describe the new step"></kendo-textbox>
      </div>

      <div class="form-row-group">
        <div class="form-row">
          <label>Work Center</label>
          <kendo-dropdownlist
            formControlName="newStepWorkCenter"
            [data]="workCenters"
            textField="name"
            valueField="code"
            [valuePrimitive]="true"
          ></kendo-dropdownlist>
        </div>

        <div class="form-row">
          <label>Sequence</label>
          <kendo-numerictextbox
            formControlName="newStepSequence"
            [min]="1"
            [step]="10"
            [format]="'n0'"
          ></kendo-numerictextbox>
        </div>
      </div>

      <div class="form-row">
        <label>Standard Time (minutes)</label>
        <kendo-numerictextbox
          formControlName="newStepStandardTime"
          [min]="0"
          [decimals]="2"
        ></kendo-numerictextbox>
      </div>

      <div class="form-row">
        <label>Work Instructions</label>
        <kendo-textarea formControlName="newStepLongText" placeholder="Detailed work instructions" [rows]="3"></kendo-textarea>
      </div>
    </div>

    <!-- Modification Data (for Modify action) -->
    <div class="form-section" *ngIf="requiresModificationData(mappingForm.get('action')?.value)">
      <h4>Modification Details</h4>
      <div class="form-row">
        <label>Modifications (JSON)</label>
        <kendo-textarea
          formControlName="modificationData"
          placeholder='{"standardTime": 30, "workCenter": "MACH02"}'
          [rows]="4"
        ></kendo-textarea>
        <span class="field-hint">Specify which fields to modify and their new values</span>
      </div>
    </div>

    <!-- Append Instructions (for AppendInstructions action) -->
    <div class="form-section" *ngIf="requiresInstructionText(mappingForm.get('action')?.value)">
      <h4>Instructions to Append</h4>
      <div class="form-row">
        <label>Additional Instructions</label>
        <kendo-textarea formControlName="newStepLongText" placeholder="Text to append to the step's instructions" [rows]="4"></kendo-textarea>
      </div>
    </div>

    <!-- Advanced Options -->
    <div class="form-section">
      <h4>Advanced Options</h4>
      <div class="form-row-group">
        <div class="form-row">
          <label>Priority</label>
          <kendo-numerictextbox
            formControlName="priority"
            [min]="-100"
            [max]="100"
            [step]="1"
            [format]="'n0'"
          ></kendo-numerictextbox>
          <span class="field-hint">Higher priority mappings are processed later (override earlier ones)</span>
        </div>

        <div class="form-row checkbox-row">
          <kendo-switch formControlName="isActive"></kendo-switch>
          <label>Active</label>
        </div>
      </div>

      <div class="form-row">
        <label>Condition (Optional)</label>
        <kendo-textbox
          formControlName="condition"
          placeholder="e.g., HasFeature('Electronics') AND BitSize > 8"
        ></kendo-textbox>
        <span class="field-hint">Additional condition for when this mapping should apply</span>
      </div>
    </div>
  </form>

  <kendo-dialog-actions>
    <button kendoButton (click)="cancelEdit()">Cancel</button>
    <button kendoButton themeColor="primary" (click)="saveMapping()" [disabled]="mappingForm.invalid">
      {{ isNew ? 'Create Mapping' : 'Save Changes' }}
    </button>
  </kendo-dialog-actions>
</kendo-dialog>
